import { formatDate } from 'core/helpers';
import { JsonObject } from 'core/types';
import { Payment, SourceType, Status } from '../enums';

export const sourceTypeFormat = (sourceType: string): SourceType => {
  return SourceType[sourceType];
};

export const signatureStatusFormat = (status: string, signPermission: boolean): Status => {
  switch (status) {
    case 'OPEN':
      return Status.OPEN;
    case 'UNSIGNED':
      return signPermission ? Status.UNSIGNED : Status.UNSIGNED_NO_PERMISSION;
    case 'PARTIALLY_SIGNED':
      return signPermission ? Status.PARTIALLY_SIGNED : Status.PARTIALLY_SIGNED_NO_PERMISSION;
    case 'SIGNATURE_RULE_MISSING':
      return Status.MISSING_SIGNATURE_RULE;
    case 'FULLY_SIGNED':
    case 'COMPLETED_SUCCESSFULLY':
      return Status.FULLY_SIGNED;
    default:
      return null;
  }
};

export const ftsStatusFormat = (status: string): Status => {
  switch (status) {
    case 'RJCT':
      return Status.ERROR;
    case 'PART':
      return Status.WARNING;
    case 'ACCP':
      return Status.SUCCESS_COMPLETE;
    default:
      return null;
  }
};

export const dbStatusFormat = (status: string): Status => {
  switch (status) {
    case 'RJCT':
      return Status.ERROR;
    case 'PART':
      return Status.WARNING;
    case 'ACCP':
    case 'ACSP':
    case 'ACWC':
    case 'ACxx':
      return Status.SUCCESS;
    case 'ACSC':
      return Status.SUCCESS_COMPLETE;
    case 'ACPT':
    case 'ACTC':
    case 'PDNG':
    case 'RCVD':
    case 'PROG':
      return Status.PROGRESS;
    default:
      return null;
  }
};

export const uploadStatusFormat = (status: string): Status => (status ? Status[status as Status] : undefined);

export class CreditTransferOrder {
  public uniqueId: string;
  public signatureStatus?: Status;
  public signatureDetails: string;
  public orderId: string;
  public controlSum: string;
  public transactionsNumber: number;
  public createdOn: string;
  public createdBy: string;
  public deletedAt?: string;
  public deletedBy?: string;
  public warnings: string[];
  public ftsStatus?: Status;
  public dbStatus?: Status;
  public paymentReportStatus?: Status;
  public uploadStatus?: Status;
  public sourceType: SourceType;
  public payment: Payment;
  public signedByUser: boolean;
  public comments?: string;
  public signableByUser?: boolean;
  public fileName?: string;
  public status?: string;
  public secondFactorRequiredForSignoff?: boolean;

  constructor(json: JsonObject) {
    this.signatureStatus = signatureStatusFormat(json.signatureStatus, json.signableByUser);
    this.uniqueId = json.uniqueId;
    this.orderId = json.orderId;
    this.controlSum = json.controlSum;
    this.transactionsNumber = json.actualNumberOfTransactions;
    this.createdOn = json.created ? formatDate(json.created, 'DD.MM.YYYY HH:mm:ss') : null;
    this.createdBy = json.createdBy.fullName;
    this.deletedAt = json.deletedAt ? formatDate(json.deletedAt, 'DD.MM.YYYY HH:mm:ss') : null;
    this.deletedBy = json.deletedBy;
    this.signatureDetails = json.signatures
      ? json.signatures.map(
          (signature: JsonObject) =>
            `${formatDate(signature.timestamp, 'DD.MM.YYYY HH:mm:ss')}, ${signature.signer.username}`
        )
      : '-';
    this.warnings = json.warnings;
    this.status = json.status;
    this.ftsStatus = ftsStatusFormat(json.ftsStatus);
    this.dbStatus = dbStatusFormat(json.paymentReportStatus);
    this.uploadStatus = uploadStatusFormat(json.paymentReportStatus);
    this.sourceType = SourceType[json.sourceType as SourceType];
    this.payment = json.salaryPayment ? Payment.SALARY : Payment.NORMAL;
    this.signedByUser = json.signedByUser;
    this.sourceType = sourceTypeFormat(json.sourceType);
    this.comments = json.comments; // backend doesn't provide it
    this.fileName = json.clientFile.filename; // backend doesn't provide it
  }
}
